"use client";

import React from "react";
import { Minus, Plus, Tag, Folder } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="aspect-square bg-gray-100 animate-pulse rounded-lg" />
        <div className="space-y-6">
          <div className="h-10 w-3/4 bg-gray-100 animate-pulse" />
          <div className="h-6 w-1/3 bg-gray-100 animate-pulse" />
          <div className="h-24 w-full bg-gray-100 animate-pulse" />
          <div className="h-12 w-full bg-gray-100 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-gray-300 bg-white p-8 text-center rounded-lg">
        <p className="text-gray-600">Unable to load product details.</p>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="border border-gray-300 bg-white p-8 text-center rounded-lg">
        <p className="text-gray-600">Product not found.</p>
      </div>
    );
  }

  const product = products[0];
  const hasDiscount =
    product.discounted_price !== undefined &&
    product.discounted_price !== null &&
    product.discounted_price > 0 &&
    product.discounted_price < product.price;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
      {}
      <div className="relative">
        <div
          className="relative border border-gray-200 bg-white overflow-hidden rounded-lg"
          style={{ boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)" }}
        >
          {product.featured_image_url ? (
            <div className="aspect-square overflow-hidden bg-gray-50">
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          ) : (
            <div className="aspect-square flex items-center justify-center bg-gray-50">
              <span className="text-gray-400 font-sans text-xs uppercase tracking-widest">
                No Image
              </span>
            </div>
          )}
          {hasDiscount && (
            <div className="absolute top-4 left-4">
              <Badge className="bg-[#e63946] text-white font-semibold uppercase tracking-wider px-3 py-1.5 rounded-lg">
                Sale
              </Badge>
            </div>
          )}
        </div>
      </div>

      {}
      <div className="flex flex-col">
        <div className="mb-4">
          <span className="font-sans text-xs uppercase tracking-[0.25em] text-[#e63946]">
            ProWhip Premium
          </span>
        </div>

        <h1
          className="font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight mb-6 text-black"
        >
          {product.title}
        </h1>

        {}
        <div className="flex items-baseline gap-4 mb-8">
          {hasDiscount ? (
            <>
              <span className="font-default font-bold text-4xl text-[#e63946]">
                ${product.discounted_price?.toFixed(2)}
              </span>
              <span className="font-default text-2xl text-gray-500 line-through">
                ${product.price.toFixed(2)}
              </span>
            </>
          ) : (
            <span className="font-default font-bold text-4xl text-[#e63946]">
              ${product.price?.toFixed(2)}
            </span>
          )}
        </div>

        {}
        {product.short_description && (
          <div
            className="text-lg leading-relaxed text-gray-600 mb-8 [&_p]:mb-3 [&_p:last-child]:mb-0"
            dangerouslySetInnerHTML={{ __html: product.short_description }}
          />
        )}

        <Separator className="bg-gray-200 mb-8" />

        {}
        <div className="mb-8">
          <AddToCartButton
            productId={product.id}
            className="w-full"
            buttonClassName="bg-[#e63946] text-white rounded-full hover:bg-[#d62839]"
            text="Add to Cart"
            showQuantity={true}
            size="lg"
          />
        </div>

        {}
        <div className="space-y-4">
          {product.categories && product.categories.length > 0 && (
            <div className="flex items-start gap-3">
              <div className="flex items-center gap-2 min-w-32">
                <Folder className="size-4 text-[#e63946]" />
                <span className="font-sans text-xs uppercase tracking-widest text-gray-500">
                  Category
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.categories.map((cat, i) => (
                  <Link
                    key={cat.id}
                    to={cat.link || "#"}
                    data-index={i}
                    className="font-sans text-xs uppercase tracking-wider text-gray-700 hover:text-[#e63946] border border-gray-300 px-3 py-1 rounded-md transition-colors"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FullDescriptionConsumer() {
  const { products, loading } = useEcommerceProducts();

  if (loading || !products || products.length === 0) return null;
  const product = products[0];
  if (!product.long_description) return null;

  return (
    <div className="mt-20 lg:mt-28">
      <div className="border-t border-gray-200 pt-12">
        <h2
          className="font-default font-bold text-3xl sm:text-4xl tracking-tight mb-8 text-black"
        >
          Product Details
        </h2>
        <div
          className="max-w-3xl text-lg leading-relaxed text-gray-600 [&_p]:mb-4 [&_p:last-child]:mb-0 [&_h2]:font-bold [&_h2]:text-2xl [&_h2]:mt-8 [&_h2]:mb-4 [&_h3]:font-bold [&_h3]:text-xl [&_h3]:mt-6 [&_h3]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1 [&_a]:text-[#e63946] [&_a:hover]:underline [&_strong]:font-bold [&_strong]:text-black"
          dangerouslySetInnerHTML={{ __html: product.long_description }}
        />
      </div>
    </div>
  );
}

export default function Product() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};

  return (
    <section data-section-id="4941"
      id="single-product-template-section"
      className="bg-white text-gray-900 relative"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
        {}
        <nav className="flex items-center gap-2 mb-12 font-sans text-xs uppercase tracking-widest text-gray-500">
          <Link to="/HomePage" className="text-black hover:text-[#e63946] transition-colors">
            Home
          </Link>
          <span>/</span>
          <Link
            to="/ShopPage"
            className="text-black hover:text-[#e63946] transition-colors"
          >
            Shop
          </Link>
          <span>/</span>
          <span className="text-[#e63946]">Product</span>
        </nav>

        <EcommerceProductsProvider wp_query={currentWPQueryParams}>
          <SingleProductConsumer />
          <FullDescriptionConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}